/**
 * CoalescencePage — Kulkarni–Joshi 2005 bubble swarm coalescence
 *
 * d_eff = d_Fritz · (1 + 0.4·ψ)^(1/3),  ψ = √(q_pore / (l_pore · v_rise))
 * Higher pore density and per-pore flow → more coalescence → larger bubbles → lower SOTE.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Activity, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { specFromLegacy, type LegacyModSpec, qPerModuleNm3h, poresPerModule, bubbleDiameterMm, bubbleRiseVelMs, sotePerMetre } from '@/lib/diffuserPhysics';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

interface ModSpec extends LegacyModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number }
const MOD_DEFAULTS: ModSpec = { diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2, waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12 };

export default function CoalescencePage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('coalescence');
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));

  useEffect(() => {
    const unsub = onPersistChange(k => { if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }); });
    return () => unsub();
  }, []);

  const R = useMemo(() => {
    const dSpec = specFromLegacy(spec);
    const T = spec.waterTempC;
    const pores_mod = poresPerModule(dSpec);
    const q_mod_Nm3h = qPerModuleNm3h(dSpec);
    const q_pore_m3s = (q_mod_Nm3h / 3600) / Math.max(1, pores_mod);
    const d_fritz_mm = bubbleDiameterMm(dSpec, T);
    const v_rise = bubbleRiseVelMs(d_fritz_mm, T);
    // Pore-to-pore distance: sqrt(active_area / pores)
    const A_diff_m2 = spec.diffuserType.includes('tube') ? 0.1 * 1.5 : Math.PI * (spec.diffuserDiamMm / 2000) ** 2;
    const l_pore_m = Math.sqrt(A_diff_m2 / Math.max(1, pores_mod / spec.diffuserCount));
    const psi = Math.sqrt(Math.max(1e-12, q_pore_m3s / (l_pore_m * Math.max(0.01, v_rise))));
    const d_eff_mm = d_fritz_mm * Math.pow(1 + 0.4 * psi, 1/3);
    const sote_fritz = sotePerMetre(d_fritz_mm);
    const sote_eff = sotePerMetre(d_eff_mm);
    return { d_fritz_mm, d_eff_mm, psi, l_pore_m, q_pore_m3s, sote_fritz, sote_eff,
      dropPct: (1 - sote_eff / sote_fritz) * 100, growthPct: (d_eff_mm / d_fritz_mm - 1) * 100 };
  }, [spec]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Activity} title="Module — Bubble Swarm Coalescence" subtitle="Kulkarni & Joshi 2005 effective bubble diameter after coalescence"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('coalescence')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="coalescence" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Fritz bubble Ø (no coalescence)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.d_fritz_mm, 2)} mm</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Effective bubble Ø</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{fmt(R.d_eff_mm, 2)} mm</div>
          <div className="text-xs text-slate-500 mt-1">+{fmt(R.growthPct, 1)}% growth</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Coalescence parameter ψ</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.psi, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">l_pore {fmt(R.l_pore_m * 1000, 1)} mm</div>
        </div>
        <div className="rounded-xl border-2 border-red-700 bg-red-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-red-300">SOTE penalty</div>
          <div className="text-2xl font-bold text-red-200 mt-1">−{fmt(R.dropPct, 1)} %</div>
          <div className="text-xs text-slate-500 mt-1">{fmt(R.sote_fritz, 2)} → {fmt(R.sote_eff, 2)} %/m</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> What this module does</div>
        <p>The formula d_eff = d_Fritz × (1 + 0.4·ψ)^(1/3) corrects the simple Fritz bubble size for the
        fact that in a dense swarm, neighbouring bubbles collide and coalesce in the first 0.1–0.3 m of rise.
        The coalescence parameter ψ scales with √(per-pore flow / (pore spacing × rise velocity)). When
        enabled, downstream pages report the larger effective bubble — and the matching lower SOTE — instead
        of the optimistic Fritz value.</p>
        <p className="italic text-slate-500 mt-2">References: Geary & Rice 1991; Kulkarni & Joshi 2005.</p>
      </div>
    </div>
  );
}
